import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { motion } from "framer-motion";
import { Check, Copy, Download, ExternalLink, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { downloadReceiptPdf } from "@/lib/receiptPdf";
import { RazorpayPay } from "./RazorpayPay";

const inr = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;
const APPS = ["Google Pay", "PhonePe", "Paytm", "BHIM", "Other UPI app"];

export const PayStep = ({ booking, onPaid, api }) => {
  const [utr, setUtr] = useState("");
  const [payer, setPayer] = useState(booking.name);
  const [app, setApp] = useState(APPS[0]);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try { await navigator.clipboard.writeText(booking.upi_id); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { toast.error("Copy failed — UPI ID: " + booking.upi_id); }
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${api}/api/site-visit/bookings/${booking.booking_id}/payment`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ utr, payer_name: payer, upi_app: app }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Could not record payment");
      onPaid(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-10">
    <RazorpayPay booking={booking} api={api} onPaid={onPaid} />
    <div className="grid lg:grid-cols-2 gap-10">
      <div className="bg-[#161614] text-[#FBFBF9] p-8 flex flex-col items-center text-center" data-testid="upi-pay-card">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#C29B38]">Pay {inr(booking.pay_now)} via UPI</p>
        <div className="mt-6 bg-white p-4" data-testid="upi-qr">
          <QRCodeSVG value={booking.upi_link} size={196} level="M" includeMargin={false} />
        </div>
        <p className="mt-4 text-sm text-stone-300">Scan with any UPI app · amount & note are pre-filled</p>
        <div className="mt-5 flex items-center gap-2 font-mono text-sm">
          <span data-testid="upi-id-text">{booking.upi_id}</span>
          <button type="button" onClick={copy} className="w-9 h-9 border border-stone-600 flex items-center justify-center hover:border-[#FBFBF9]" aria-label="Copy UPI ID" data-testid="upi-copy-btn">{copied ? <Check size={14} /> : <Copy size={14} />}</button>
        </div>
        <p className="font-mono text-[10px] tracking-[0.2em] text-stone-500 mt-1">{booking.upi_payee} · AXIS BANK</p>
        <motion.a whileTap={{ scale: 0.97 }} href={booking.upi_link} className="btn-dark !bg-[#FBFBF9] !text-stone-900 !border-[#FBFBF9] mt-6 lg:!hidden" data-testid="upi-open-app-btn"><Smartphone size={16} /> Open UPI app</motion.a>
        <p className="mt-6 text-[11px] text-stone-500 leading-relaxed">Booking {booking.booking_id} · payment note is pre-filled so we can match it to you.</p>
      </div>

      <form onSubmit={submit} className="space-y-6" data-testid="utr-form">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#B85C38]">Step 3 · After paying</p>
          <h3 className="font-serif text-2xl text-stone-900 mt-2">Enter the UPI reference</h3>
          <p className="mt-2 text-sm text-stone-600 font-light leading-relaxed">Your UPI app shows a 12-digit UTR / transaction ID on the success screen. Paste it here and we will issue your receipt.</p>
        </div>
        <label className="field block relative">
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">UTR / transaction reference</span>
          <input required value={utr} onChange={(e) => setUtr(e.target.value)} inputMode="numeric" className="mt-2 w-full bg-transparent border-b border-stone-300 py-3 font-mono text-lg tracking-widest text-stone-900 placeholder:text-stone-300 focus:outline-none" placeholder="4312 8899 0011" data-testid="utr-input" />
          <span aria-hidden="true" className="field-line" />
        </label>
        <div className="grid sm:grid-cols-2 gap-6">
          <label className="field block relative">
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">Paid from (name on UPI)</span>
            <input value={payer} onChange={(e) => setPayer(e.target.value)} className="mt-2 w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none" data-testid="payer-input" />
            <span aria-hidden="true" className="field-line" />
          </label>
          <label className="field block relative">
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">UPI app</span>
            <select value={app} onChange={(e) => setApp(e.target.value)} className="mt-2 w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none" data-testid="upi-app-select">
              {APPS.map((a) => <option key={a}>{a}</option>)}
            </select>
            <span aria-hidden="true" className="field-line" />
          </label>
        </div>
        <motion.button whileTap={{ scale: 0.97 }} type="submit" disabled={busy || utr.replace(/\s/g, "").length < 6} className="btn-dark disabled:opacity-50" data-testid="utr-submit-btn">
          {busy ? <span className="blocks" aria-label="Saving"><i /><i /><i /></span> : <>I have paid — issue receipt</>}
        </motion.button>
        <p className="text-[11px] text-stone-500 leading-relaxed">We verify every payment against the bank statement within one working day. The receipt is provisional until then.</p>
      </form>
    </div>
    </div>
  );
};

export const ReceiptStep = ({ booking, onReset }) => {
  const [busy, setBusy] = useState(false);
  const p = booking.payment || {};
  const verified = !!p.verified;
  const emailed = booking.email_status?.customer === "sent";
  const download = async () => {
    setBusy(true);
    try { await downloadReceiptPdf(booking); } catch { toast.error("Could not build the PDF. Please try again."); } finally { setBusy(false); }
  };
  return (
    <div className="grid lg:grid-cols-12 gap-10" data-testid="receipt-step">
      <div className="lg:col-span-7">
        <motion.span initial={{ scale: 0, rotate: -20 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 300, damping: 18 }} className="inline-flex w-14 h-14 items-center justify-center bg-[#2D6A4F] text-white"><Check size={26} /></motion.span>
        <h3 className="font-serif text-3xl lg:text-4xl text-stone-900 mt-6">Visit booked. Receipt issued.</h3>
        <p className="mt-4 text-stone-600 font-light leading-relaxed max-w-lg">
          Receipt <span className="font-mono text-stone-900" data-testid="receipt-no">{booking.receipt_no}</span> for {inr(p.amount)} {verified ? <>is <span className="text-[#2D6A4F]">confirmed</span> (Razorpay payment <span className="font-mono">{p.payment_id}</span>).</> : <>has been recorded against UPI reference <span className="font-mono">{p.utr}</span>.</>}
          {emailed ? ` A copy is on its way to ${booking.email}.` : " We could not email the copy right now — please download it below."}
          {verified ? ` We will call ${booking.phone} within one working day to fix the time.` : ` We confirm the payment against our bank statement within one working day and then call ${booking.phone} to fix the time.`}
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <motion.button whileTap={{ scale: 0.97 }} onClick={download} disabled={busy} className="btn-dark disabled:opacity-60" data-testid="receipt-download-btn">{busy ? <span className="blocks"><i /><i /><i /></span> : <>Download receipt PDF <Download size={16} /></>}</motion.button>
          <a href={`https://wa.me/919747575166?text=${encodeURIComponent(`Hi, I booked a site visit (${booking.booking_id}, receipt ${booking.receipt_no}) for ${booking.place} on ${booking.visit_date}.`)}`} target="_blank" rel="noopener noreferrer" className="btn-ghost" data-testid="receipt-whatsapp-btn">Message us on WhatsApp <ExternalLink size={14} /></a>
        </div>
        <button type="button" onClick={onReset} className="mt-8 link-line font-mono text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-stone-900" data-testid="receipt-new-booking-btn">Book another visit</button>
      </div>
      <dl className="lg:col-span-5 bg-white border border-stone-200 p-6 font-mono text-xs divide-y divide-stone-200" data-testid="receipt-summary">
        {[["Booking", booking.booking_id], ["Location", booking.place], ["Date", booking.visit_date], ["Total fee", inr(booking.total)], ["Paid now", inr(p.amount)], ["Balance", booking.balance ? inr(booking.balance) : "Nil"], ["Status", verified ? "Confirmed" : "Pending confirmation"]].map(([k, v]) => (
          <div key={k} className="flex justify-between gap-4 py-3"><dt className="text-stone-500 uppercase tracking-wider">{k}</dt><dd className="text-right text-stone-900">{v}</dd></div>
        ))}
      </dl>
    </div>
  );
};
